import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { statusPhrase } from '../util/statusPhrase.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { attachItemInfoToggle } from '../components/ItemInfoPanel.js';
import { showPromptDialog } from '../components/ConfirmDialog.js';
import { formatCrowns } from '../util/formatBig.js';
import { EQUIP_PICKER_CSS, slotPickerHtml, wireSlotPickers, type SlotChangeResult } from '../util/equipSlots.js';
import {
  bindVerifyPromptButtons,
  isEmailNotVerifiedError,
  verifyEmailPromptHtml,
} from '../components/VerifyEmailPrompt.js';
import {
  ITEM_CATEGORY_LABELS,
  ITEM_TIER_LABELS,
  type CharacterDto,
  type InventoryItemDto,
  type ItemCategory,
  type LoadoutDto,
} from '@rex-mortis/shared';

// Debounce handle for the inventory search box's full-page rebuild.
let invSearchTimer: number | null = null;

const CATEGORY_ORDER: ItemCategory[] = [
  'melee',
  'ranged',
  'armor_chest',
  'armor_head',
  'armor_shield',
  'amulet',
  'tool',
  'elixir',
  'tonic',
  'food',
  'material',
  'chest',
  'misc',
];

/**
 * Use rules are now DERIVED from the inventory DTO (which carries `usable`,
 * `cooldownClass`, `removesInfirmary`, `removesDungeon` straight from the
 * catalog effect) rather than a hand-maintained allowlist. Every consumable
 * with an effect gets a Use button automatically. The server remains the only
 * real enforcement; this is purely for showing/hiding the button + disabled state.
 */
type CooldownClass = 'elixir' | 'tonic' | 'medical' | 'food';
interface UseRule {
  /** Cooldown bucket, or null for a usable item with no cooldown. */
  cooldown: CooldownClass | null;
  /** Statuses under which this item CAN be used (others reject server-side). */
  allowedStatuses: Array<'OKAY' | 'INFIRMARY' | 'DUNGEON'>;
}

/** Derive the Use rule for an inventory item from its DTO flags. Null = not usable. */
function deriveUseRule(it: InventoryItemDto): UseRule | null {
  if (!it.usable) return null;
  // Dungeon-clearing tools (e.g. manacle pick) are ONLY usable in the Dungeon.
  // Infirmary-clearing items (e.g. Friar's Aid) work both at large and in the
  // Infirmary. Everything else is normal-status only.
  // Healing items (the 'medical' cooldown class — bandages, salves, tonics that
  // restore Life) are usable in the Infirmary: bandage back to full and you
  // discharge yourself. Dedicated revives also work there; everything else is
  // normal-status only.
  const allowedStatuses: Array<'OKAY' | 'INFIRMARY' | 'DUNGEON'> = it.removesDungeon
    ? ['DUNGEON']
    : it.removesInfirmary || it.cooldownClass === 'medical'
      ? ['OKAY', 'INFIRMARY']
      : ['OKAY'];
  return { cooldown: it.cooldownClass ?? null, allowedStatuses };
}

function cooldownActive(char: CharacterDto, kind: CooldownClass): boolean {
  const field =
    kind === 'elixir'
      ? char.elixirCooldownUntil
      : kind === 'tonic'
        ? char.tonicCooldownUntil
        : kind === 'food'
          ? char.foodCooldownUntil
          : char.medicalCooldownUntil;
  return !!field && Date.parse(field) > Date.now();
}

interface FlashState {
  kind: 'success' | 'danger' | 'warn' | 'verify';
  message: string;
}

/** Compact "Currently Equipped" panel for the Inventory page — mirrors the
 *  Profile's loadout view so players can see their gear without leaving the bag.
 *  Each slot is a dropdown of the eligible items you own, so you can swap gear
 *  right here without scrolling the bag for the per-card Equip buttons. */
function equipmentPanelHtml(loadout: LoadoutDto | null, items: InventoryItemDto[]): string {
  if (!loadout) return '';
  const cell = (
    label: string,
    slotKey: 'primary' | 'secondary' | 'helm' | 'chest' | 'amulet' | 'rod' | 'spear' | 'pickaxe' | 'axe' | 'basket',
    slot: LoadoutDto['primary'],
  ): string => `
    <div class="equip-cell${slot.itemName ? '' : ' empty'}">
      <div class="equip-cell-slot">${label}</div>
      ${slotPickerHtml(slotKey, slot, items)}
    </div>`;
  // All five tool slots always show (empty if no tool owned), so players can see
  // the gathering professions exist and go find the tools out in the realm.
  const toolCell = (
    label: string,
    slotKey: 'rod' | 'spear' | 'pickaxe' | 'axe' | 'basket',
    slot: LoadoutDto['primary'],
  ): string => cell(label, slotKey, slot);
  return `
    <details class="equip-panel" open>
      <summary>Currently Equipped</summary>
      <div class="equip-cells">
        ${cell('Weapon', 'primary', loadout.primary)}
        ${cell('Off-hand', 'secondary', loadout.secondary)}
        ${cell('Helm', 'helm', loadout.helm)}
        ${cell('Chest', 'chest', loadout.chest)}
        ${cell('Amulet', 'amulet', loadout.amulet)}
        ${toolCell('Fishing Rod', 'rod', loadout.rod)}
        ${toolCell('Hunting Spear', 'spear', loadout.spear)}
        ${toolCell('Pickaxe', 'pickaxe', loadout.pickaxe)}
        ${toolCell('Axe', 'axe', loadout.axe)}
        ${toolCell('Foraging Basket', 'basket', loadout.basket)}
      </div>
    </details>`;
}

export async function renderInventory(root: HTMLElement): Promise<void> {
  let items: InventoryItemDto[] = [];
  let loadout: LoadoutDto | null = null;
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const inv = await api.listInventory();
    items = inv.items;
  } catch {
    // tolerated
  }
  // Loadout drives the "Equipped" panel + highlighting. Best-effort: the page
  // still renders if it fails.
  try {
    loadout = (await api.getLoadout()).loadout;
  } catch {
    /* tolerated */
  }
  /** Slugs currently worn across the four equip slots — used to flag inventory
   *  cards so the player doesn't accidentally sell their only copy. */
  const equippedSet = (): Set<string> => {
    const s = new Set<string>();
    if (loadout) {
      for (const slot of [loadout.primary, loadout.secondary, loadout.helm, loadout.chest, loadout.amulet, loadout.rod, loadout.spear, loadout.pickaxe, loadout.axe, loadout.basket]) {
        if (slot?.itemSlug) s.add(slot.itemSlug);
      }
    }
    return s;
  };

  let flash: FlashState | null = null;

  /** Filter state — persists for the lifetime of this page mount. */
  const filter = {
    search: '',
    category: 'all' as 'all' | string,
    tier: 'all' as 'all' | string,
  };

  /** Collapsed category headings — persisted across sessions in localStorage. */
  const COLLAPSE_KEY = 'rexmortis.inventory.collapsed';
  let collapsed: Set<string>;
  try {
    collapsed = new Set<string>(JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '[]'));
  } catch {
    collapsed = new Set<string>();
  }
  const persistCollapsed = (): void => {
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...collapsed]));
    } catch {
      /* storage unavailable — non-fatal, collapse just won't persist */
    }
  };
  const applyFilter = (list: InventoryItemDto[]): InventoryItemDto[] => {
    const q = filter.search.trim().toLowerCase();
    return list.filter((it) => {
      if (filter.category !== 'all' && it.category !== filter.category) return false;
      if (filter.tier !== 'all' && it.tier !== filter.tier) return false;
      if (q && !(it.name.toLowerCase().includes(q) || it.slug.toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  };

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      const totalCount = items.length;
      const visibleCount = applyFilter(items).length;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/inventory.webp" alt="" />
        <h1>Inventory</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Everything you own and a few things you really shouldn't.
        </p>

        ${equipmentPanelHtml(loadout, items)}

        <div class="inv-filters">
          <div class="inv-filter-field">
            <label for="inv-search">Search</label>
            <input type="search" id="inv-search" placeholder="Item name…" value="${escapeAttr(filter.search)}" />
          </div>
          <div class="inv-filter-field">
            <label for="inv-cat">Category</label>
            <select id="inv-cat">
              <option value="all" ${filter.category === 'all' ? 'selected' : ''}>All categories</option>
              ${CATEGORY_ORDER.map((c) => `
                <option value="${c}" ${filter.category === c ? 'selected' : ''}>${ITEM_CATEGORY_LABELS[c]}</option>
              `).join('')}
            </select>
          </div>
          <div class="inv-filter-field">
            <label for="inv-tier">Tier</label>
            <select id="inv-tier">
              <option value="all" ${filter.tier === 'all' ? 'selected' : ''}>All tiers</option>
              ${(['crude','common','fine','masterwork','legendary','artifact'] as const)
                .map((t) => `<option value="${t}" ${filter.tier === t ? 'selected' : ''}>${ITEM_TIER_LABELS[t]}</option>`).join('')}
            </select>
          </div>
          ${
            visibleCount !== totalCount
              ? `<button class="btn ghost" id="inv-clear" style="align-self: end;">Clear (${visibleCount}/${totalCount})</button>`
              : `<div class="inv-count" style="align-self: end;">${totalCount} item${totalCount === 1 ? '' : 's'}</div>`
          }
        </div>

        <div id="flash"></div>
        <div id="inv"></div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .inv-thumb {
          width: 44px;
          height: 44px;
          flex-shrink: 0;
          object-fit: cover;
          border-radius: var(--radius);
          border: 1px solid var(--c-rule);
          background: var(--c-parchment-dark);
          cursor: pointer;
          transition: border-color 0.12s ease, transform 0.12s ease;
        }
        .inv-thumb:hover {
          border-color: var(--c-gold);
          transform: scale(1.04);
        }
        .inv-section { margin-bottom: var(--s-5); }
        .inv-section h2 {
          font-family: var(--font-display);
          color: var(--c-oxblood);
          border-bottom: 1px solid var(--c-rule);
          padding-bottom: var(--s-1);
          margin-bottom: var(--s-3);
        }
        .item-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: var(--s-3);
        }
        .item-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex;
          flex-direction: column;
        }
        .item-card .row {
          display: flex; justify-content: space-between; align-items: baseline;
        }
        .item-card h3 { margin: 0; font-size: 1rem; }
        .item-card .qty {
          font-family: var(--font-numeric);
          color: var(--c-oxblood);
          font-weight: bold;
        }
        .item-card .blurb {
          font-style: italic;
          color: var(--c-ink-soft);
          font-size: 0.88rem;
          margin: 4px 0 6px 0;
        }
        .item-card .tier-badge {
          font-family: var(--font-display);
          font-size: 0.78rem;
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          padding: 1px 6px;
          border-radius: 3px;
        }
        .item-card .tier-badge.crude       { color: var(--c-ink-soft); }
        .item-card .tier-badge.common      { color: var(--c-ink); }
        .item-card .tier-badge.fine        { color: var(--c-success); }
        .item-card .tier-badge.masterwork  { color: var(--c-oxblood); }
        /* Higher tiers escalate into premium "seals" so rarity reads at a
           glance and progression feels earned. Gold is used AS the fill (not
           gold text), which also guarantees contrast in both themes. */
        .item-card .tier-badge.legendary,
        .item-card .tier-badge.artifact,
        .item-card .tier-badge.mythic {
          position: relative;
          overflow: hidden;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.045em;
        }
        /* Legendary — embossed burnished-gold coin. */
        .item-card .tier-badge.legendary {
          background: linear-gradient(155deg, var(--c-gold-pale) 0%, var(--c-gold-bright) 45%, var(--c-gold) 100%);
          color: var(--c-on-gold);
          border-color: var(--c-rule-strong);
          box-shadow: inset 0 1px 0 rgba(255, 250, 225, 0.6), inset 0 -2px 3px rgba(120, 80, 15, 0.3), 0 1px 2px rgba(0, 0, 0, 0.25);
          text-shadow: 0 1px 0 rgba(255, 248, 225, 0.55);
        }
        /* Artifact — radiant, hotter gold with an outer glow + glint sweep. */
        .item-card .tier-badge.artifact {
          background: linear-gradient(155deg, #fff4c9 0%, #ffce5a 38%, #f0a83a 70%, #c9801f 100%);
          color: var(--c-on-gold);
          border-color: #9a5e16;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 0 9px rgba(245, 165, 55, 0.65), 0 1px 2px rgba(0, 0, 0, 0.25);
          text-shadow: 0 1px 0 rgba(255, 245, 215, 0.5);
        }
        /* Mythic — the apex: a glowing amethyst gem with a brighter sweep. */
        .item-card .tier-badge.mythic {
          background: linear-gradient(155deg, #f4e6ff 0%, #c89dff 32%, #9156e9 68%, #5e2bb8 100%);
          color: #fff;
          border-color: #4a1f93;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5), 0 0 11px rgba(165, 95, 255, 0.75), 0 1px 2px rgba(0, 0, 0, 0.3);
          text-shadow: 0 1px 2px rgba(40, 0, 80, 0.55);
        }
        .item-card .tier-badge.artifact::after,
        .item-card .tier-badge.mythic::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(115deg, transparent 35%, rgba(255, 255, 255, 0.55) 50%, transparent 65%);
          transform: translateX(-130%);
          animation: tier-shimmer 3.4s ease-in-out infinite;
          pointer-events: none;
        }
        .item-card .tier-badge.mythic::after {
          animation-duration: 2.7s;
          background: linear-gradient(115deg, transparent 35%, rgba(255, 255, 255, 0.72) 50%, transparent 65%);
        }
        @media (prefers-reduced-motion: reduce) {
          .item-card .tier-badge.artifact::after,
          .item-card .tier-badge.mythic::after { animation: none; opacity: 0; }
        }
        .item-card .use {
          margin-top: var(--s-2);
          align-self: flex-start;
          padding: 4px 12px;
          font-size: 0.85rem;
        }
        .empty {
          font-style: italic;
          color: var(--c-ink-soft);
        }
        .inv-filters {
          display: grid;
          grid-template-columns: minmax(180px, 1.6fr) minmax(160px, 1fr) minmax(140px, 1fr) auto;
          gap: var(--s-3);
          background: var(--c-parchment);
          background-image: var(--parchment-texture);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          margin-bottom: var(--s-4);
          box-shadow: var(--shadow-soft);
        }
        @media (max-width: 720px) {
          .inv-filters {
            grid-template-columns: 1fr 1fr;
          }
          .inv-filters > :last-child { grid-column: 1 / -1; }
        }
        .inv-filter-field {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .inv-filter-field label {
          font-family: var(--font-display);
          font-size: 0.85rem;
          letter-spacing: 0.04em;
          color: var(--c-ink-soft);
          margin-bottom: 3px;
        }
        .inv-filter-field input,
        .inv-filter-field select {
          font: inherit;
          font-family: var(--font-body);
          padding: var(--s-2) var(--s-3);
          /* Colours come from the global pinned cream cell in styles/pages.css. */
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          box-shadow: var(--shadow-press);
          min-width: 0;
          width: 100%;
        }
        .inv-filter-field input:focus,
        .inv-filter-field select:focus {
          outline: none;
          border-color: var(--c-gold);
          box-shadow: var(--shadow-press), 0 0 0 3px rgba(212, 169, 60, 0.25);
        }
        .inv-count {
          font-family: var(--font-display);
          color: var(--c-ink-soft);
          font-size: 0.95rem;
          padding-bottom: 8px;
        }
        /* Currently-equipped panel (mirrors the Profile loadout view). */
        .equip-panel {
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          background: var(--c-parchment);
          margin-bottom: var(--s-3);
          padding: var(--s-2) var(--s-3);
        }
        .equip-panel summary {
          cursor: pointer;
          font-family: var(--font-display);
          color: var(--c-oxblood);
          user-select: none;
        }
        .equip-cells {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: var(--s-2);
          margin-top: var(--s-2);
        }
        .equip-cell {
          border: 1px solid var(--c-rule-soft);
          border-radius: var(--radius);
          background: var(--c-parchment-dark);
          padding: 6px 9px;
          min-width: 0;
        }
        /* De-emphasise ONLY the slot label for an empty slot — never the cell
           itself. The picker's popup menu lives inside this cell, so fading the
           whole cell made the open dropdown translucent AND gave it an opacity
           stacking context that glitched it over the cells below. */
        .equip-cell.empty .equip-cell-slot { opacity: 0.6; }
        .equip-cell-slot {
          font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em;
          color: var(--c-ink-soft);
        }
        .equip-cell-item {
          font-family: var(--font-display); color: var(--c-ink);
          overflow-wrap: anywhere;
        }
        ${EQUIP_PICKER_CSS}
        /* Highlight inventory cards whose item is currently equipped. */
        .item-card.equipped {
          border-color: var(--c-gold);
          box-shadow: 0 0 0 1px var(--c-gold);
        }
        .equipped-badge {
          font-size: 0.72rem;
          color: var(--c-gold-text, #9c6b16);
          font-family: var(--font-numeric);
          font-weight: 700;
          margin-left: 6px;
        }
      `;
      main.appendChild(style);

      // Flash.
      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        if (flash.kind === 'verify') {
          flashRoot.innerHTML = verifyEmailPromptHtml(flash.message);
          bindVerifyPromptButtons(flashRoot);
        } else {
          const div = document.createElement('div');
          div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
          div.textContent = flash.message;
          flashRoot.appendChild(div);
        }
      }

      // Wire filter controls. The search box debounces the full-page rebuild so
      // a large late-game inventory doesn't re-render (and re-wire every card's
      // listeners) on every keystroke; anchored() preserves the caret.
      main.querySelector<HTMLInputElement>('#inv-search')?.addEventListener('input', (e) => {
        filter.search = (e.target as HTMLInputElement).value;
        if (invSearchTimer !== null) window.clearTimeout(invSearchTimer);
        invSearchTimer = window.setTimeout(() => {
          invSearchTimer = null;
          anchored(renderMain);
        }, 150);
      });
      main.querySelector<HTMLSelectElement>('#inv-cat')?.addEventListener('change', (e) => {
        filter.category = (e.target as HTMLSelectElement).value;
        anchored(renderMain);
      });
      main.querySelector<HTMLSelectElement>('#inv-tier')?.addEventListener('change', (e) => {
        filter.tier = (e.target as HTMLSelectElement).value;
        anchored(renderMain);
      });
      main.querySelector<HTMLButtonElement>('#inv-clear')?.addEventListener('click', () => {
        filter.search = '';
        filter.category = 'all';
        filter.tier = 'all';
        anchored(renderMain);
      });

      const inv = main.querySelector<HTMLDivElement>('#inv')!;
      if (items.length === 0) {
        inv.innerHTML = '<p class="empty">Your packs are empty. Go cause a little trouble.</p>';
        return;
      }

      const visibleItems = applyFilter(items);
      if (visibleItems.length === 0) {
        inv.innerHTML = '<p class="empty">No items match those filters.</p>';
        return;
      }

      for (const cat of CATEGORY_ORDER) {
        const inCat = visibleItems.filter((i) => i.category === cat);
        if (inCat.length === 0) continue;

        const section = document.createElement('section');
        section.className = 'inv-section';

        const grid = document.createElement('div');
        grid.className = 'item-grid';

        // Collapsible heading: click to fold the category away. State persists
        // per-category in localStorage so the player's layout sticks.
        const startCollapsed = collapsed.has(cat);
        const h2 = document.createElement('h2');
        h2.className = 'collapse-head';
        h2.setAttribute('role', 'button');
        h2.setAttribute('tabindex', '0');
        h2.style.cssText = 'cursor:pointer; user-select:none; display:flex; align-items:center; gap:8px;';
        h2.innerHTML =
          `<span class="chev" style="display:inline-block; width:1em; transition:transform .15s;">${startCollapsed ? '▸' : '▾'}</span>` +
          `<span>${escapeHtml(ITEM_CATEGORY_LABELS[cat])}</span>` +
          `<span class="soft" style="font-weight:400; font-size:0.85rem;">(${inCat.length})</span>`;
        if (startCollapsed) grid.style.display = 'none';
        const toggle = () => {
          const nowCollapsed = grid.style.display !== 'none';
          grid.style.display = nowCollapsed ? 'none' : '';
          (h2.querySelector('.chev') as HTMLElement).textContent = nowCollapsed ? '▸' : '▾';
          if (nowCollapsed) collapsed.add(cat);
          else collapsed.delete(cat);
          persistCollapsed();
        };
        h2.addEventListener('click', toggle);
        h2.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
        });
        section.appendChild(h2);

        for (const it of inCat) {
          const rule = deriveUseRule(it);
          let useButton = '';
          if (rule) {
            const cdActive = rule.cooldown ? cooldownActive(char, rule.cooldown) : false;
            const statusOk = rule.allowedStatuses.includes(
              char.status as 'OKAY' | 'INFIRMARY' | 'DUNGEON',
            );
            const disabled = cdActive || !statusOk;
            const cdLabel = rule.cooldown ? labelOf(rule.cooldown) : '';
            const why = cdActive
              ? `${cdLabel} cooldown`
              : !statusOk
                ? `not usable while ${statusPhrase(char.status)}`
                : '';
            useButton = `
              <button class="btn use" data-use="${it.slug}" ${disabled ? 'disabled' : ''}
                title="${escapeHtml(why)}">
                ${cdActive ? `${cdLabel} CD` : 'Use'}
              </button>
            `;
          }

          const card = document.createElement('div');
          card.className = 'item-card';
          const isEquipped = equippedSet().has(it.slug);
          if (isEquipped) card.classList.add('equipped');
          // Show an Equip button for weapons / armor / shields / helms.
          let equipButton = '';
          let equipSlot: 'primary' | 'secondary' | 'helm' | 'chest' | 'amulet' | 'rod' | 'spear' | 'pickaxe' | 'axe' | 'basket' | null = null;
          if (it.category === 'melee' || it.category === 'ranged') equipSlot = 'primary';
          else if (it.category === 'armor_shield') equipSlot = 'secondary';
          else if (it.category === 'armor_head') equipSlot = 'helm';
          else if (it.category === 'armor_chest') equipSlot = 'chest';
          else if (it.category === 'amulet') equipSlot = 'amulet';
          else if (it.category === 'tool') {
            if (it.slug.startsWith('rod_')) equipSlot = 'rod';
            else if (it.slug.startsWith('spear_')) equipSlot = 'spear';
            else if (it.slug.startsWith('pickaxe_')) equipSlot = 'pickaxe';
            else if (it.slug.startsWith('axe_')) equipSlot = 'axe';
            else if (it.slug.startsWith('basket_')) equipSlot = 'basket';
          }
          if (equipSlot) {
            equipButton = `<button class="btn ghost" data-equip="${it.slug}" data-slot="${equipSlot}" style="padding:4px 12px; font-size:0.85rem;">Equip</button>`;
          }

          card.innerHTML = `
            <div class="row" style="align-items:flex-start;">
              <div style="display:flex; align-items:center; gap: 10px; min-width: 0; flex: 1 1 auto;">
                <img class="inv-thumb" src="/img/item-thumbs/${it.slug}.webp" alt="" width="44" height="44" loading="lazy" onerror="this.style.display='none'" data-item-name="${it.slug}" title="Click for full info" />
                <h3 style="margin:0; min-width: 0;" data-item-name="${it.slug}" title="Click for full info">${escapeHtml(it.name)}</h3>
              </div>
              <span class="qty">×${it.qty}</span>
            </div>
            <div><span class="tier-badge ${it.tier}">${ITEM_TIER_LABELS[it.tier]}</span>${isEquipped ? ' <span class="equipped-badge">★ Equipped</span>' : ''}</div>
            <p class="blurb">${escapeHtml(it.blurb)}</p>
            <div class="row" style="font-size:0.85rem; color: var(--c-ink-soft);">
              <span>worth ${formatCrowns(it.baseValue)} crowns each</span>
              <span>got ${formatRelative(it.acquiredAt)}</span>
            </div>
            <div style="display:flex; gap: var(--s-2); margin-top: var(--s-2); flex-wrap: wrap;">
              ${useButton}
              ${equipButton}
              <button class="btn ghost" data-list="${it.slug}" style="padding:4px 12px; font-size:0.85rem;">List</button>
              <button class="btn ghost" data-sell="${it.slug}" style="padding:4px 12px; font-size:0.85rem;" title="${isEquipped ? 'You have this equipped — selling your last one will unequip it' : 'Sell instantly to the merchant at the floor price'}">Sell</button>
            </div>
          `;
          // Make the item name clickable — opens the info dropdown inside the card.
          // Both the thumbnail and the name open the info modal. The attach
          // function is idempotent — wiring two targets to the same slug just
          // gives the player two visual hit-zones.
          card.querySelectorAll<HTMLElement>(`[data-item-name="${it.slug}"]`).forEach((el) => {
            attachItemInfoToggle(el, it.slug, { containerEl: card });
          });
          grid.appendChild(card);
        }
        section.appendChild(grid);
        inv.appendChild(section);
      }

      // Wire up Use buttons.
      inv.querySelectorAll<HTMLButtonElement>('button[data-use]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.use!;
          // Disable all use buttons during the round-trip to prevent double-click double-use.
          inv.querySelectorAll<HTMLButtonElement>('button[data-use]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.useItem({ itemSlug: slug });
            store.set({ character: r.character });
            const refreshed = await api.listInventory();
            items = refreshed.items;
            flash = buildFlash(r);
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Use failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Wire up Equip buttons.
      inv.querySelectorAll<HTMLButtonElement>('button[data-equip]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.equip!;
          const slot = btn.dataset.slot as 'primary' | 'secondary' | 'helm' | 'chest' | 'amulet' | 'rod' | 'spear' | 'pickaxe' | 'axe' | 'basket';
          inv.querySelectorAll<HTMLButtonElement>('button[data-equip]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.equip({ slot, itemSlug: slug });
            store.set({ character: r.character });
            // Same in-place refresh as the slot-picker dropdowns — swap just the
            // equipment panel + card highlights rather than rebuilding the page.
            await onSlotChange({
              kind: 'success',
              message: `Equipped ${r.itemName} to ${slot}${r.swappedOut ? ` (swapped out ${r.swappedOut})` : ''}.`,
            });
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Equip failed.';
            await onSlotChange({ kind: 'danger', message: msg });
          } finally {
            // Cards aren't rebuilt now, so re-enable the Equip buttons ourselves.
            inv.querySelectorAll<HTMLButtonElement>('button[data-equip]').forEach((b) => (b.disabled = false));
          }
        });
      });

      // Wire up the per-slot equip pickers in the "Currently Equipped" panel.
      // Equipping should NOT tear down the whole page — a full re-render reloads
      // the banner and scroll-jumps you to the top, which reads as a reload.
      // Instead we surgically swap just the equipment panel, re-sync the card
      // highlights, and drop the result into the #flash banner in place.
      const setFlash = (result: SlotChangeResult): void => {
        const flashRoot = main.querySelector<HTMLDivElement>('#flash');
        if (!flashRoot) return;
        flashRoot.innerHTML = '';
        const div = document.createElement('div');
        div.className = `alert ${result.kind === 'danger' ? 'danger' : 'success'}`;
        div.textContent = result.message;
        flashRoot.appendChild(div);
      };
      // Re-sync the ★/border "equipped" cues on the inventory cards without
      // rebuilding them. Each card is identified by its Sell button's slug.
      const refreshEquippedHighlights = (): void => {
        const set = equippedSet();
        main.querySelectorAll<HTMLElement>('.item-card').forEach((card) => {
          const slug = card.querySelector<HTMLButtonElement>('button[data-sell]')?.dataset.sell;
          if (!slug) return;
          const on = set.has(slug);
          card.classList.toggle('equipped', on);
          const tierRow = card.querySelector('.tier-badge')?.parentElement;
          if (tierRow) {
            const badge = tierRow.querySelector('.equipped-badge');
            if (on && !badge) tierRow.insertAdjacentHTML('beforeend', ' <span class="equipped-badge">★ Equipped</span>');
            else if (!on && badge) badge.remove();
          }
        });
      };
      const onSlotChange = async (result: SlotChangeResult): Promise<void> => {
        if (result.kind === 'success') {
          try { const r = await api.listInventory(); items = r.items; } catch { /* keep stale */ }
          try { loadout = (await api.getLoadout()).loadout; } catch { /* keep stale */ }
          const panel = main.querySelector('.equip-panel');
          if (panel) {
            const tmp = document.createElement('div');
            tmp.innerHTML = equipmentPanelHtml(loadout, items);
            const fresh = tmp.firstElementChild as HTMLElement | null;
            if (fresh) {
              panel.replaceWith(fresh);
              wireSlotPickers(fresh, onSlotChange);
              refreshEquippedHighlights();
            } else {
              flash = result; anchored(renderMain); return;
            }
          } else {
            flash = result; anchored(renderMain); return;
          }
        }
        setFlash(result);
      };
      wireSlotPickers(main, onSlotChange);

      // Wire up Sell-to-merchant buttons.
      inv.querySelectorAll<HTMLButtonElement>('button[data-sell]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.sell!;
          const item = items.find((i) => i.slug === slug);
          if (!item) return;
          const qtyStr = await showPromptDialog({
            title: `Sell ${item.name}`,
            message: `Sell to the merchant at the floor price.\nHow many to sell? (you have ${item.qty})`,
            defaultValue: String(item.qty),
            inputType: 'number',
            min: 1,
            max: item.qty,
            confirmLabel: 'Sell',
          });
          if (qtyStr === null) return;
          const qty = Math.floor(Number(qtyStr));
          if (!Number.isFinite(qty) || qty < 1 || qty > item.qty) {
            flash = { kind: 'danger', message: 'Invalid quantity.' };
            anchored(renderMain);
            return;
          }
          inv.querySelectorAll<HTMLButtonElement>('button[data-sell]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.sellToMerchant(slug, qty);
            store.set({ character: r.character });
            const refreshed = await api.listInventory();
            items = refreshed.items;
            // Selling your last equipped copy can unequip it server-side — keep
            // the equipped panel/highlights in sync.
            try { loadout = (await api.getLoadout()).loadout; } catch { /* keep stale */ }
            flash = {
              kind: 'success',
              message: `Sold ${r.qtySold}× ${r.itemName} for ${formatCrowns(r.totalPaid)} crowns (${formatCrowns(r.pricePerItem)} each).`,
            };
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Sell failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Wire up List buttons.
      inv.querySelectorAll<HTMLButtonElement>('button[data-list]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.list!;
          const item = items.find((i) => i.slug === slug);
          if (!item) return;

          const qtyStr = await showPromptDialog({
            title: `List ${item.name}`,
            message: `List on the market.\nHow many to list? (you have ${item.qty})`,
            defaultValue: String(item.qty),
            inputType: 'number',
            min: 1,
            max: item.qty,
            confirmLabel: 'Next',
          });
          if (qtyStr === null) return;
          const qty = Math.floor(Number(qtyStr));
          if (!Number.isFinite(qty) || qty < 1 || qty > item.qty) {
            flash = { kind: 'danger', message: 'Invalid quantity.' };
            anchored(renderMain);
            return;
          }

          const priceStr = await showPromptDialog({
            title: `Price ${item.name}`,
            message: `Price per item (crowns)?\nVendor base value: ${formatCrowns(item.baseValue)}.`,
            defaultValue: String(Math.max(1, Math.floor(item.baseValue * 1.1))),
            inputType: 'number',
            min: 1,
            confirmLabel: 'List it',
          });
          if (priceStr === null) return;
          if (!/^\d+$/.test(priceStr.trim())) {
            flash = { kind: 'danger', message: 'Price must be a positive integer.' };
            anchored(renderMain);
            return;
          }

          inv.querySelectorAll<HTMLButtonElement>('button[data-list]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.listOnMarket({
              itemSlug: slug,
              qty,
              pricePer: priceStr.trim(),
            });
            const refreshed = await api.listInventory();
            items = refreshed.items;
            flash = {
              kind: 'success',
              message: `Listed ${r.listing.qty}× ${r.listing.itemName} at ${formatCrowns(r.listing.pricePer)} crowns each. Expires ${new Date(r.listing.expiresAt).toLocaleDateString()}.`,
            };
          } catch (err) {
            if (isEmailNotVerifiedError(err)) {
              flash = { kind: 'verify', message: err.message };
            } else {
              const msg = err instanceof HttpError ? err.message : 'Listing failed.';
              flash = { kind: 'danger', message: msg };
            }
          } finally {
            anchored(renderMain);
          }
        });
      });
    };

    anchored(renderMain);
  });
}

function buildFlash(r: import('@rex-mortis/shared').UseItemResponse): FlashState {
  if (r.overdosed) {
    return {
      kind: 'danger',
      message: `Overdose! ${r.itemName} hits you the wrong way. Hospitalised, spirit shattered, cooldown stretched to a day.`,
    };
  }
  const parts: string[] = [`${r.itemName} used.`];
  const r2 = r.restored;
  const restored: string[] = [];
  if (r2.vigor > 0) restored.push(`+${r2.vigor} vigor`);
  if (r2.resolve > 0) restored.push(`+${r2.resolve} resolve`);
  if (r2.spirit > 0) restored.push(`+${r2.spirit} spirit`);
  if (r2.life > 0) restored.push(`+${r2.life} life`);
  if (restored.length > 0) parts.push(restored.join(', ') + '.');
  if (r.infirmaryReducedSeconds > 0) {
    const m = Math.floor(r.infirmaryReducedSeconds / 60);
    const s = r.infirmaryReducedSeconds % 60;
    const t = m > 0 ? `${m}m ${s}s` : `${s}s`;
    parts.push(`Shaved ${t} off your Infirmary stay.`);
  }
  if (r.removedStatus === 'INFIRMARY') parts.push('Released from the Infirmary.');
  if (r.removedStatus === 'DUNGEON') parts.push('Slipped from the Dungeon.');
  // Overfill / wastage notices — surface as a soft warn rather than success.
  if (r.warnings.length > 0) {
    return { kind: 'warn', message: [...parts, ...r.warnings].join(' ') };
  }
  return { kind: 'success', message: parts.join(' ') };
}

function labelOf(kind: 'elixir' | 'tonic' | 'medical' | 'food'): string {
  return kind === 'elixir' ? 'Elixir' : kind === 'tonic' ? 'Tonic' : kind === 'food' ? 'Food' : 'Medical';
}

/** Escape a string for inclusion inside an HTML attribute value. */
function escapeAttr(s: string): string {
  return escapeHtml(s);
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const sec = Math.max(0, (Date.now() - t) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86_400)}d ago`;
}
